<script setup lang="ts">
import { ref, watchEffect } from "vue";
import type { ISatSetupProps } from "@chatfood/design-system";
import { SatSetup } from "@chatfood/design-system";
import { bemBuilder } from "@chatfood/core-utils";
import { useBranding } from "~/composables/use-branding";
import { useLocale } from "~/composables/use-locale";
import { useAppURI } from "~/composables/use-app-uri";
import { ModalHub } from "~/components/modal-hub";
import { CheckoutCallback } from "~/components/checkout-callback";
import { NuxtRoutedPage } from "~/components/nuxt-routed-page";
import { CookiesConsent } from "~/components/cookies-consent";
import { ReviewForm } from "~/components/review-form";
import { useBusinessStore } from "~/stores/business";
import { onMounted, useAnalytics, useHead } from "#imports";

const css = bemBuilder("app");

const business = useBusinessStore();

const { setDefaultLanguage, extractTranslations } = useLocale();
const { getHeadTags } = useBranding();

setDefaultLanguage(business.locale);

useHead(
  getHeadTags(
    business.designTokens,
    business.logo,
    business.styles?.customCssSheet,
  ),
);

const satelliteLocale = ref<ISatSetupProps["fullLocale"]>();

watchEffect(async () => {
  try {
    const translations = await extractTranslations("app.satellite_defaults");
    satelliteLocale.value = translations as ISatSetupProps["fullLocale"];
  } catch {
    //
  }
});

onMounted(() => {
  if (business.isMobileApp) {
    document.documentElement.style.setProperty("--overscroll-behavior", "none");
  }

  useAppURI().persist();
  useAnalytics().trackEvent({
    name: "startJourney",
    payload: {},
  });
});
</script>

<template>
  <SatSetup :full-locale="satelliteLocale">
    <div :class="css()">
      <NuxtLayout>
        <NuxtRoutedPage />
      </NuxtLayout>
      <ModalHub />
      <CookiesConsent />
      <ReviewForm v-if="business.id" :business-id="business.id" />
    </div>
    <CheckoutCallback />
  </SatSetup>
</template>

<style lang="scss">
html,
body {
  /* to disable the overscroll bounce feature of browsers */
  overscroll-behavior: var(--overscroll-behavior, initial);
  -webkit-overflow-scrolling: auto;
}
</style>

<style lang="scss" scoped>
.app {
  min-width: 375px;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  white-space: pre-line;

  @media (width <= $breakpoint-sm) {
    background-color: var(--color-base-100);
  }
}
</style>
